import React, { useState } from 'react';
import { ChevronsDown } from 'lucide-react';
import { Badge, FloatingPanel, RobbieMark, RobbieState } from '../ui';
import RobbieChatInput, { RobbieChatInputProps } from './RobbieChatInput';

/**
 * v2 floating Robbie panel (TASK-107) — the dashboard/home presence, ported
 * from `Dashboard v2.dc.html`: an expanded panel (mark + insight count +
 * collapse chevrons, message card, input with mic) that collapses to a pill
 * (mark + "N insights"). Positioning belongs to the caller (the mock pins
 * `absolute bottom/right-[22px] w-[clamp(280px,26vw,340px)]`), so the panel
 * overlays without blocking the page. Chrome only — wiring is EPIC-03.
 */
export interface RobbieFloatingPanelProps {
  /** Mascot state; the mock's default presence is insight (glowing eyes + beacon). */
  state?: RobbieState;
  insightCount?: number;
  messages?: React.ReactNode[];
  input?: RobbieChatInputProps;
  /** Controlled expansion; omit for internal state (starts expanded). */
  expanded?: boolean;
  defaultExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  className?: string;
}

export const RobbieFloatingPanel: React.FC<RobbieFloatingPanelProps> = ({
  state = 'insight',
  insightCount = 0,
  messages = [],
  input,
  expanded,
  defaultExpanded = true,
  onToggle,
  className = '',
}) => {
  const [internal, setInternal] = useState(defaultExpanded);
  const isOpen = expanded ?? internal;
  const toggle = () => {
    onToggle?.(!isOpen);
    if (expanded === undefined) setInternal(!isOpen);
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={`Open Robbie — ${insightCount} insights`}
        className={`flex items-center gap-s3 self-start rounded-round border border-robbie-border bg-ai-panel py-s3 pl-s4 pr-s5 shadow-floating hover:border-robbie ${className}`}
      >
        <RobbieMark size="xs" state={state} />
        <span className="whitespace-nowrap text-body-sm font-semibold text-ink">
          {insightCount === 1 ? '1 insight' : `${insightCount} insights`}
        </span>
      </button>
    );
  }

  return (
    <FloatingPanel ai className={`overflow-hidden border-robbie-border ${className}`}>
      <div className="flex items-center gap-s4 border-b border-robbie/[0.12] px-[16px] py-[12px]">
        <RobbieMark size="sm" state={state} aria-label={`Robbie — ${state}`} />
        <span className="text-card-header text-ink">Robbie</span>
        {insightCount > 0 && (
          <Badge tone="robbie" mark={false} className="whitespace-nowrap border-robbie-border bg-surface text-[11px] font-semibold">
            {insightCount === 1 ? '1 insight' : `${insightCount} insights`}
          </Badge>
        )}
        <button type="button" onClick={toggle} aria-label="Collapse Robbie" className="ml-auto text-ink-muted hover:text-ink-secondary">
          <ChevronsDown className="h-[15px] w-[15px]" />
        </button>
      </div>
      {messages.length > 0 && (
        <div className="flex flex-col gap-s3 px-[12px] pt-s4">
          {messages.map((m, i) => (
            <div key={i} className="rounded-[9px] border border-robbie-border-soft bg-surface px-[12px] py-[9px] text-body-md leading-[1.55] text-ink-body">
              {m}
            </div>
          ))}
        </div>
      )}
      <div className="p-[12px]">
        <RobbieChatInput {...input} />
      </div>
    </FloatingPanel>
  );
};

export default RobbieFloatingPanel;
